import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { DEPLOYMENT_KEYS } from 'utils/constants'
import { normalizeSchedules, getScheduleId } from '../../../../Instruments/utils/multiSchedule'
import {
  getScheduleStartTypeLabel,
  getStartLabel,
  getExpireLabel,
  getRepeatLabel,
  getVisibleFieldLabel,
} from '../utils/studyTimelineScheduleDisplay'
import {
  COHORT_TYPE_LABELS,
  INSTRUMENT_DETAIL,
  INSTRUMENT_EMPTY_VALUE,
  INSTRUMENT_TYPE_DISPLAY,
  SCHEDULE,
  LABELS,
} from '../utils/constants'
import { hasSchedulingEngineFeature } from 'utils/studyConfigUtils'
import { hasEngineScheduleDeploy } from '../utils/engineScheduleProjection'
import { getInstancePermissionsResumeEditLabel } from '../utils/engineInstancePermissionsDisplay'
import {
  getScheduleExceedsStudyDurationTooltip,
  isScheduleTruncatedByStudyDuration,
} from '../utils/scheduleStudyDurationNotice'
import { formatTriggerKindLabel } from '../projection/inferTriggerKind'

const getSentToLabel = cohort => {
  if (!cohort) return COHORT_TYPE_LABELS.all_participants
  const type = Array.isArray(cohort) ? cohort[0]?.type : cohort.type
  return COHORT_TYPE_LABELS[type] || COHORT_TYPE_LABELS.all_participants
}

const getPlacementLabel = displayLocation => {
  if (!displayLocation) return INSTRUMENT_EMPTY_VALUE
  if (Array.isArray(displayLocation)) return displayLocation.join(', ')
  return String(displayLocation)
}

const getLegacyResumeEditLabel = schedule => {
  if (!schedule || typeof schedule !== 'object') return INSTRUMENT_EMPTY_VALUE
  const timeoutWindow = schedule.timeout_window || {}
  const hasReview =
    schedule.review && (schedule.review.value != null || schedule.review.type != null)
  const hasEdit = schedule.edit && schedule.edit.value != null
  const hasDraft = schedule.draft && schedule.draft.value != null
  const hasTimeout =
    timeoutWindow.review?.value != null ||
    timeoutWindow.edit?.value != null ||
    timeoutWindow.draft?.value != null
  return hasReview || hasEdit || hasDraft || hasTimeout ? INSTRUMENT_DETAIL.YES : INSTRUMENT_EMPTY_VALUE
}

const getResumeEditLabel = schedule => {
  if (!schedule || typeof schedule !== 'object') return INSTRUMENT_EMPTY_VALUE
  if (hasEngineScheduleDeploy(schedule)) {
    return getInstancePermissionsResumeEditLabel(schedule.instance_permissions)
  }
  return getLegacyResumeEditLabel(schedule)
}

const getTriggersLabel = schedules => {
  for (const schedule of schedules) {
    const chainDeployment = schedule?.chain_deployment_info
    if (!chainDeployment) continue
    const prevIds = chainDeployment.prev_instrument_id
    if (Array.isArray(prevIds) && prevIds.length > 0) {
      return `${prevIds.length} ${SCHEDULE.INSTRUMENT}${prevIds.length > 1 ? 's' : ''}`
    }
    if (DEPLOYMENT_KEYS.chainDeployByVisit in chainDeployment) return SCHEDULE.VISIT
  }
  return INSTRUMENT_EMPTY_VALUE
}

const getTypeDisplay = type => {
  if (!type) return INSTRUMENT_EMPTY_VALUE
  const key = String(type).toUpperCase()
  return INSTRUMENT_TYPE_DISPLAY[key] || type
}

const formatDetailValue = value => (value != null && value !== '' ? value : INSTRUMENT_EMPTY_VALUE)

const TimelineScheduleItem = ({
  schedule,
  index,
  open,
  onToggle,
  visitIdToName,
  instrumentIdToName,
  useAbsoluteStartDate,
  absoluteStartDate,
  instrumentData,
  metadata,
  onEditSchedule,
  canEdit,
  isActiveOnDay,
  studyDurationDays,
  scheduleAnchorContext,
}) => {
  const scheduleIdLabel = getScheduleId(schedule)
  const scheduleTitle = scheduleIdLabel
    ? `Schedule ${index + 1} (${scheduleIdLabel})`
    : `Schedule ${index + 1}`
  const startTypeLabel = getScheduleStartTypeLabel(schedule)
  const exceedsStudyDuration =
    studyDurationDays != null &&
    isScheduleTruncatedByStudyDuration(schedule, studyDurationDays, {
      ...scheduleAnchorContext,
      instrumentData,
    })
  const exceedsStudyDurationTooltip = exceedsStudyDuration
    ? getScheduleExceedsStudyDurationTooltip(studyDurationDays)
    : null

  const scheduleRows = [
    [
      INSTRUMENT_DETAIL.START,
      getStartLabel(
        schedule,
        visitIdToName,
        useAbsoluteStartDate,
        absoluteStartDate,
        instrumentData,
        instrumentIdToName,
      ),
    ],
    [INSTRUMENT_DETAIL.EXPIRE, getExpireLabel(schedule)],
    [INSTRUMENT_DETAIL.REPEAT, getRepeatLabel(schedule)],
    [INSTRUMENT_DETAIL.VISIBLE, getVisibleFieldLabel(schedule, metadata)],
    ...(hasEngineScheduleDeploy(schedule)
      ? [[INSTRUMENT_DETAIL.RESUME_EDIT, getResumeEditLabel(schedule)]]
      : []),
  ]

  return (
    <div
      className={[
        'study-timeline-schedule-item',
        open ? 'open' : '',
        isActiveOnDay ? 'study-timeline-schedule-item--on-day' : '',
      ]
        .filter(Boolean)
        .join(' ')}>
      <button
        type='button'
        className='study-timeline-schedule-item-header'
        onClick={onToggle}
        aria-expanded={open}>
        <span className='study-timeline-schedule-item-title'>
          <i
            className={`fas ${open ? 'fa-caret-down' : 'fa-caret-right'} study-timeline-schedule-chevron`}
            aria-hidden='true'
          />
          <span>{scheduleTitle}</span>
        </span>
        <span className='study-timeline-schedule-item-header-meta'>
          {exceedsStudyDurationTooltip && (
            <span
              className='study-timeline-schedule-duration-notice'
              role='img'
              aria-label={exceedsStudyDurationTooltip}
              tabIndex={0}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}>
              <i className='fas fa-info-circle' aria-hidden='true' />
            </span>
          )}
          <span className='study-timeline-schedule-type-tag'>{startTypeLabel}</span>
        </span>
        {exceedsStudyDurationTooltip && (
          <span className='study-timeline-schedule-duration-tooltip' role='tooltip'>
            {exceedsStudyDurationTooltip}
          </span>
        )}
      </button>
      {open && (
        <div className='study-timeline-schedule-item-body'>
          <div className='study-timeline-detail-content'>
            {scheduleRows.map(([label, value]) => (
              <div className='study-timeline-detail-row' key={label}>
                <span className='study-timeline-detail-label'>{label}:</span>
                <span className='study-timeline-detail-value'>{formatDetailValue(value)}</span>
              </div>
            ))}
          </div>
          {canEdit && (
            <div className='study-timeline-schedule-item-actions'>
              <button
                type='button'
                className='study-timeline-edit-schedule-btn'
                onClick={e => {
                  e.stopPropagation()
                  onEditSchedule(index)
                }}>
                {INSTRUMENT_DETAIL.EDIT_SCHEDULE}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

TimelineScheduleItem.propTypes = {
  schedule: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
  visitIdToName: PropTypes.object,
  instrumentIdToName: PropTypes.object,
  useAbsoluteStartDate: PropTypes.bool,
  absoluteStartDate: PropTypes.string,
  instrumentData: PropTypes.object,
  metadata: PropTypes.object,
  onEditSchedule: PropTypes.func,
  canEdit: PropTypes.bool,
  isActiveOnDay: PropTypes.bool,
  studyDurationDays: PropTypes.number,
  scheduleAnchorContext: PropTypes.object,
}

const InstrumentDetailContent = ({
  instrument,
  selectedDay,
  studyID,
  studyVersionID,
  visitIdToName = {},
  instrumentIdToName = {},
  useAbsoluteStartDate = false,
  absoluteStartDate = null,
  onEditSchedule,
  activeScheduleIndices,
  currentStudy,
  studyDurationDays = null,
  scheduleAnchorContext = {},
}) => {
  const instrumentData = instrument.instrumentData || instrument
  const metadata = instrumentData.metadata || {}
  const schedules = normalizeSchedules(metadata.schedule)
  const instrumentId = instrumentData.id || instrument.instrumentId

  const activeIndexSet = useMemo(() => {
    if (activeScheduleIndices instanceof Set) return activeScheduleIndices
    if (Array.isArray(activeScheduleIndices)) return new Set(activeScheduleIndices)
    return new Set()
  }, [activeScheduleIndices])

  const resolveExpandedIndices = () => {
    const indices = new Set()
    if (
      typeof instrument.scheduleIndex === 'number' &&
      instrument.scheduleIndex >= 0 &&
      instrument.scheduleIndex < schedules.length
    ) {
      indices.add(instrument.scheduleIndex)
    }
    if (activeIndexSet.size > 0) {
      schedules.forEach((_, index) => {
        if (activeIndexSet.has(index)) indices.add(index)
      })
    }
    if (indices.size === 0 && schedules.length > 0) {
      indices.add(0)
    }
    return indices
  }

  const [expandedIndices, setExpandedIndices] = useState(resolveExpandedIndices)

  useEffect(() => {
    setExpandedIndices(resolveExpandedIndices())
  }, [selectedDay, schedules.length, activeIndexSet, instrument.scheduleIndex])

  const showOnDayLegend = schedules.length > 1 && activeIndexSet.size > 0

  const renderRow = (label, value) => (
    <div className='study-timeline-detail-row' key={label}>
      <span className='study-timeline-detail-label'>{label}:</span>
      <span className='study-timeline-detail-value'>{formatDetailValue(value)}</span>
    </div>
  )

  const focusedScheduleIndex =
    typeof instrument.scheduleIndex === 'number' &&
    instrument.scheduleIndex >= 0 &&
    instrument.scheduleIndex < schedules.length
      ? instrument.scheduleIndex
      : 0
  const primarySchedule = schedules[focusedScheduleIndex] || schedules[0] || {}
  const cohort = primarySchedule.cohort

  const projectionRows = []
  if (instrument.scheduleId) {
    projectionRows.push([LABELS.SCHEDULE_ID, instrument.scheduleId])
  }
  if (instrument.triggerKind) {
    projectionRows.push([LABELS.TRIGGER_KIND, formatTriggerKindLabel(instrument.triggerKind)])
  }

  const detailsRows = [
    ...projectionRows,
    [INSTRUMENT_DETAIL.TYPE, getTypeDisplay(instrumentData.type)],
    [INSTRUMENT_DETAIL.SENT_TO, getSentToLabel(cohort)],
    [INSTRUMENT_DETAIL.PLACEMENT, getPlacementLabel(metadata.display_location)],
    [INSTRUMENT_DETAIL.RESUME_EDIT, getResumeEditLabel(primarySchedule)],
    [INSTRUMENT_DETAIL.SCORING, metadata.has_scoring ? INSTRUMENT_DETAIL.YES : INSTRUMENT_DETAIL.NO],
    [INSTRUMENT_DETAIL.TRIGGERS, getTriggersLabel(schedules)],
    [INSTRUMENT_DETAIL.PRIORITY, metadata.priority],
    [INSTRUMENT_DETAIL.GAME_PTS, metadata.gamification_points],
  ]

  const showAllSchedulesLink = !hasSchedulingEngineFeature(currentStudy)
  const allSchedulesUrl =
    showAllSchedulesLink && studyID && studyVersionID && instrumentId
      ? `/studies/${studyID}/${studyVersionID}/instruments/${instrumentId}/schedules`
      : null

  const canEdit = !!(typeof onEditSchedule === 'function' && instrumentId)

  const handleEditSchedule = scheduleIndex => {
    if (canEdit) {
      onEditSchedule(instrumentId, scheduleIndex)
    }
  }

  return (
    <div className='study-timeline-instrument-detail-content'>
      <div className='study-timeline-instrument-detail-section'>
        <div className='study-timeline-detail-content'>
          {detailsRows.map(([label, value]) => renderRow(label, value))}
        </div>
      </div>
      <div className='study-timeline-instrument-detail-section'>
        <div className='study-timeline-instrument-detail-subheader study-timeline-instrument-schedule-header'>
          <span>{INSTRUMENT_DETAIL.SECTION_SCHEDULES}</span>
          {allSchedulesUrl && schedules.length > 0 && (
            <Link to={allSchedulesUrl} className='study-timeline-all-schedules-link'>
              {INSTRUMENT_DETAIL.ALL_SCHEDULES}
            </Link>
          )}
        </div>
        {showOnDayLegend && (
          <div className='study-timeline-schedule-on-day-legend study-timeline-legend-item'>
            <span className='legend-color schedule-on-day' />
            <span>{INSTRUMENT_DETAIL.SCHEDULE_ON_DAY_LEGEND}</span>
          </div>
        )}
        {schedules.length === 0 ? (
          <div className='study-timeline-detail-content'>
            <p className='study-timeline-no-event'>No schedule configured for this instrument.</p>
          </div>
        ) : (
          <div className='study-timeline-schedule-list'>
            {schedules.map((schedule, index) => (
              <TimelineScheduleItem
                key={getScheduleId(schedule) || `schedule-${index}`}
                schedule={schedule}
                index={index}
                open={expandedIndices.has(index)}
                onToggle={() =>
                  setExpandedIndices(prev => {
                    const next = new Set(prev)
                    if (next.has(index)) next.delete(index)
                    else next.add(index)
                    return next
                  })
                }
                visitIdToName={visitIdToName}
                instrumentIdToName={instrumentIdToName}
                useAbsoluteStartDate={useAbsoluteStartDate}
                absoluteStartDate={absoluteStartDate}
                instrumentData={instrumentData}
                metadata={metadata}
                canEdit={canEdit}
                onEditSchedule={handleEditSchedule}
                isActiveOnDay={activeIndexSet.has(index)}
                studyDurationDays={studyDurationDays}
                scheduleAnchorContext={scheduleAnchorContext}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

InstrumentDetailContent.propTypes = {
  instrument: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    instrumentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    instrumentData: PropTypes.object,
    label: PropTypes.string,
    scheduleIndex: PropTypes.number,
  }).isRequired,
  selectedDay: PropTypes.number.isRequired,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  visitIdToName: PropTypes.object,
  instrumentIdToName: PropTypes.object,
  useAbsoluteStartDate: PropTypes.bool,
  absoluteStartDate: PropTypes.string,
  onEditSchedule: PropTypes.func,
  activeScheduleIndices: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(PropTypes.number),
  ]),
  currentStudy: PropTypes.object,
  studyDurationDays: PropTypes.number,
  scheduleAnchorContext: PropTypes.object,
}

export default InstrumentDetailContent
